import { MessageFactory, UID_BYTES_PER_ELEMENT, UINT8_BYTES_PER_ELEMENT, BOOLEAN_BYTES_PER_ELEMENT, calcStringByteLength, calcObjectByteLength, calcArrayByteLength, BusMessage } from "flavr-common";
/**
 * Publishes the current camera transform for rendering-related systems.
 */
export class CameraUpdateMessage extends BusMessage {
    get byteLength() {
        return CameraUpdateMessage.MAX_BYTE_LENGTH;
    }
    constructor(position = [0, 0, 0], orientation = [0, 0, 0, 1], direction = [1, 0, 0], platformScale) {
        super();
        this.position = position;
        this.orientation = orientation;
        this.direction = direction;
        this.platformScale = platformScale;
    }
    serialization(stream) {
        this.position = stream.number3(this.position);
        this.orientation = stream.number4(this.orientation);
        this.direction = stream.number3(this.direction);
        this.platformScale = stream.float32(this.platformScale);
    }
    static is(msg) {
        return msg.type === CameraUpdateMessage.typestatic;
    }
}
CameraUpdateMessage.typestatic = "CameraUpdateMessage";
CameraUpdateMessage.subjectstatic = "renderer";
CameraUpdateMessage.MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT * 3 + // position
    Float32Array.BYTES_PER_ELEMENT * 4 + // orientation
    Float32Array.BYTES_PER_ELEMENT * 3 + // direction
    Float32Array.BYTES_PER_ELEMENT; // platformScale
MessageFactory.register(CameraUpdateMessage);
/**
 * Publishes per-frame timing data for rendering-related systems.
 */
export class FrameUpdateMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === FrameUpdateMessage.typestatic;
    }
}
FrameUpdateMessage.typestatic = "FrameUpdateMessage";
FrameUpdateMessage.subjectstatic = "renderer";
MessageFactory.register(FrameUpdateMessage);
// Tells the renderer to set the clipFade property on instanceMeshes.
// Only affects models with a PrimitiveMesh-facet..
// target=0 => fade in
// target=1 => fade until center of instance
// target=2 => fade out
/**
 * Updates clip-fade rendering parameters.
 */
export class SetClipFadeMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Uint8Array.BYTES_PER_ELEMENT;
    }
    constructor(uid, target = 0) {
        super();
        this.uid = uid;
        this.target = target;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.target = stream.uint8(this.target);
    }
    static is(msg) {
        return msg.type === SetClipFadeMessage.typestatic;
    }
}
SetClipFadeMessage.typestatic = "SetClipFadeMessage";
SetClipFadeMessage.subjectstatic = "renderer";
MessageFactory.register(SetClipFadeMessage);
// Tells the renderer to change the transformation of sub mesh of a model.
// Only affects models with a Mesh-facet.
/**
 * Updates the transform of a submesh inside a renderable mesh.
 */
export class SetSubMeshTransformMessage extends BusMessage {
    get byteLength() {
        return SetSubMeshTransformMessage.BYTE_LENGTH + calcStringByteLength(this.submesh);
    }
    constructor(uid, submesh, position = [0, 0, 0], orientation, scale) {
        super();
        this.uid = uid;
        this.submesh = submesh;
        this.position = position;
        this.orientation = orientation;
        this.scale = scale;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.submesh = stream.utf16String(this.submesh);
        this.position = stream.number3(this.position);
        this.orientation = stream.number4Safe(this.orientation);
        this.scale = stream.number3Safe(this.scale);
    }
    static is(msg) {
        return msg.type === SetSubMeshTransformMessage.typestatic;
    }
}
SetSubMeshTransformMessage.typestatic = "SetSubMeshTransformMessage";
SetSubMeshTransformMessage.subjectstatic = "renderer";
SetSubMeshTransformMessage.BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 3);
MessageFactory.register(SetSubMeshTransformMessage);
// Tells the renderer to change the visibility of a room
/**
 * Changes visibility of a room in the renderer.
 */
export class ChangeRoomVisibilityMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(uid, visible = false) {
        super();
        this.uid = uid;
        this.visible = visible;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.visible = stream.boolean(this.visible);
    }
    static is(msg) {
        return msg.type === ChangeRoomVisibilityMessage.typestatic;
    }
}
ChangeRoomVisibilityMessage.typestatic = "ChangeRoomVisibilityMessage";
ChangeRoomVisibilityMessage.subjectstatic = "renderer";
MessageFactory.register(ChangeRoomVisibilityMessage);
/**
 * Requests attaching a room representation to a model.
 */
export class AttachRoomToModelMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // roomUid
            UID_BYTES_PER_ELEMENT + // modelUid
            BOOLEAN_BYTES_PER_ELEMENT + // attach
            Float32Array.BYTES_PER_ELEMENT * 16);
    }
    constructor(roomUid, modelUid, attach, transform) {
        super();
        this.roomUid = roomUid;
        this.modelUid = modelUid;
        this.attach = attach;
        this.transform = transform;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.modelUid = stream.uid(this.modelUid);
        this.attach = stream.boolean(this.attach);
        this.transform = stream.numberFloat32ArraySafe(16, this.transform);
    }
    static is(msg) {
        return msg.type === AttachRoomToModelMessage.typestatic;
    }
}
AttachRoomToModelMessage.typestatic = "AttachRoomToModelMessage";
AttachRoomToModelMessage.subjectstatic = "renderer";
MessageFactory.register(AttachRoomToModelMessage);
/**
 * Updates the active skybox rotation.
 */
export class SetSkyboxRotation extends BusMessage {
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT;
    }
    constructor(yaw) {
        super();
        this.yaw = yaw;
    }
    serialization(stream) {
        this.yaw = stream.float32(this.yaw);
    }
    static is(msg) {
        return msg.type === SetSkyboxRotation.typestatic;
    }
}
SetSkyboxRotation.typestatic = "SetSkyboxRotation";
SetSkyboxRotation.subjectstatic = "renderer";
MessageFactory.register(SetSkyboxRotation);
/**
 * Changes tooltip info visibility.
 */
export class ChangeTooltipInfoVisibilityMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // uid
            BOOLEAN_BYTES_PER_ELEMENT); // visible
    }
    constructor(uid, visible = false) {
        super();
        this.uid = uid;
        this.visible = visible;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.visible = stream.boolean(this.visible);
    }
    static is(msg) {
        return msg.type === ChangeTooltipInfoVisibilityMessage.typestatic;
    }
}
ChangeTooltipInfoVisibilityMessage.typestatic = "ChangeTooltipInfoVisibilityMessage";
ChangeTooltipInfoVisibilityMessage.subjectstatic = "renderer";
MessageFactory.register(ChangeTooltipInfoVisibilityMessage);
/**
 * Changes the anchor point used for tooltip placement.
 */
export class ChangeTooltipAnchorPointMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // uid
            3 * Float32Array.BYTES_PER_ELEMENT); // anchorPoint
    }
    constructor(uid, anchorPoint) {
        super();
        this.uid = uid;
        this.anchorPoint = anchorPoint;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.anchorPoint = stream.number3(this.anchorPoint);
    }
    static is(msg) {
        return msg.type === ChangeTooltipAnchorPointMessage.typestatic;
    }
}
ChangeTooltipAnchorPointMessage.typestatic = "ChangeTooltipAnchorPointMessage";
ChangeTooltipAnchorPointMessage.subjectstatic = "renderer";
MessageFactory.register(ChangeTooltipAnchorPointMessage);
/**
 * Adds highlights for the specified models.
 */
export class AddHighlightForModelsMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // controllerUid
            calcArrayByteLength(this.modelUids) + // modelUids
            UINT8_BYTES_PER_ELEMENT // color
        );
    }
    constructor(controllerUid, modelUids, color) {
        super();
        this.controllerUid = controllerUid;
        this.modelUids = modelUids;
        this.color = color;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
        this.modelUids = stream.array(this.modelUids);
        this.color = stream.uint8Safe(this.color);
    }
    static is(msg) {
        return msg.type === AddHighlightForModelsMessage.typestatic;
    }
}
AddHighlightForModelsMessage.typestatic = "AddHighlightForModelsMessage";
AddHighlightForModelsMessage.subjectstatic = "renderer";
MessageFactory.register(AddHighlightForModelsMessage);
/**
 * Removes highlights from the specified models.
 */
export class RemoveHighlightsFromModelsMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // controllerUid
            calcArrayByteLength(this.modelUids) // modelUids
        );
    }
    constructor(controllerUid, modelUids) {
        super();
        this.controllerUid = controllerUid;
        this.modelUids = modelUids;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
        this.modelUids = stream.array(this.modelUids);
    }
    static is(msg) {
        return msg.type === RemoveHighlightsFromModelsMessage.typestatic;
    }
}
RemoveHighlightsFromModelsMessage.typestatic = "RemoveHighlightsFromModelsMessage";
RemoveHighlightsFromModelsMessage.subjectstatic = "renderer";
MessageFactory.register(RemoveHighlightsFromModelsMessage);
/**
 * Creates a world marker.
 */
export class CreateMarkerMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // markerUid
            Uint32Array.BYTES_PER_ELEMENT + // baseColor
            calcStringByteLength(this.avataNameOrTextureUrl) +
            BOOLEAN_BYTES_PER_ELEMENT); // isAvatarName
    }
    constructor(markerUid, baseColor, avataNameOrTextureUrl, isAvatarName = true) {
        super();
        this.markerUid = markerUid;
        this.baseColor = baseColor;
        this.avataNameOrTextureUrl = avataNameOrTextureUrl;
        this.isAvatarName = isAvatarName;
    }
    serialization(stream) {
        this.markerUid = stream.uid(this.markerUid);
        this.baseColor = stream.int32(this.baseColor);
        this.avataNameOrTextureUrl = stream.utf16String(this.avataNameOrTextureUrl);
        this.isAvatarName = stream.boolean(this.isAvatarName);
    }
    static is(msg) {
        return msg.type === CreateMarkerMessage.typestatic;
    }
}
CreateMarkerMessage.typestatic = "CreateMarkerMessage";
CreateMarkerMessage.subjectstatic = "renderer";
MessageFactory.register(CreateMarkerMessage);
/**
 * Deletes a world marker.
 */
export class DeleteMarkerMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT; // markerUid
    }
    constructor(markerUid) {
        super();
        this.markerUid = markerUid;
    }
    serialization(stream) {
        this.markerUid = stream.uid(this.markerUid);
    }
    static is(msg) {
        return msg.type === DeleteMarkerMessage.typestatic;
    }
}
DeleteMarkerMessage.typestatic = "DeleteMarkerMessage";
DeleteMarkerMessage.subjectstatic = "renderer";
MessageFactory.register(DeleteMarkerMessage);
/**
 * Updates the target that a marker points to.
 */
export class UpdateMarkerTargetMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // markerUid
            3 * Float32Array.BYTES_PER_ELEMENT + // position
            UID_BYTES_PER_ELEMENT); // room
    }
    /**
     * @param markerUid the uid of the marker whose target has changed
     * @param position the position of the marker's target in the coordinate system of the room the target resides in.
     * @param roomUid the uid of the room the target in currently in.
     */
    constructor(markerUid, position, roomUid) {
        super();
        this.markerUid = markerUid;
        this.position = position;
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.markerUid = stream.uid(this.markerUid);
        this.position = stream.number3(this.position);
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === UpdateMarkerTargetMessage.typestatic;
    }
}
UpdateMarkerTargetMessage.typestatic = "UpdateMarkerTargetMessage";
UpdateMarkerTargetMessage.subjectstatic = "renderer";
MessageFactory.register(UpdateMarkerTargetMessage);
/**
 * Enables or disables the marker radar.
 */
export class EnableMarkerRadarMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT; // enable
    }
    constructor(enable) {
        super();
        this.enable = enable;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === EnableMarkerRadarMessage.typestatic;
    }
}
EnableMarkerRadarMessage.typestatic = "EnableMarkerRadarMessage";
EnableMarkerRadarMessage.subjectstatic = "renderer";
MessageFactory.register(EnableMarkerRadarMessage);
/**
 * Changes the active microphone device.
 */
export class ChangeMicrophoneDeviceMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.deviceId);
    }
    constructor(deviceId) {
        super();
        this.deviceId = deviceId;
    }
    serialization(stream) {
        this.deviceId = stream.utf16String(this.deviceId);
    }
    static is(msg) {
        return msg.type === ChangeMicrophoneDeviceMessage.typestatic;
    }
}
ChangeMicrophoneDeviceMessage.typestatic = "ChangeMicrophoneDeviceMessage";
ChangeMicrophoneDeviceMessage.subjectstatic = "general";
MessageFactory.register(ChangeMicrophoneDeviceMessage);
/**
 * Changes the active camera device.
 */
export class ChangeCameraDeviceMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.deviceId);
    }
    constructor(deviceId) {
        super();
        this.deviceId = deviceId;
    }
    serialization(stream) {
        this.deviceId = stream.utf16String(this.deviceId);
    }
    static is(msg) {
        return msg.type === ChangeCameraDeviceMessage.typestatic;
    }
}
ChangeCameraDeviceMessage.typestatic = "ChangeCameraDeviceMessage";
ChangeCameraDeviceMessage.subjectstatic = "general";
MessageFactory.register(ChangeCameraDeviceMessage);
/**
 * Reports microphone permission changes.
 */
export class ChangeMicrophonePermissionsMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT * 2;
    }
    constructor(allowedLocal, allowedRemote) {
        super();
        this.allowedLocal = allowedLocal;
        this.allowedRemote = allowedRemote;
    }
    serialization(stream) {
        this.allowedLocal = stream.booleanSafe(this.allowedLocal);
        this.allowedRemote = stream.booleanSafe(this.allowedRemote);
    }
    static is(msg) {
        return msg.type === ChangeMicrophonePermissionsMessage.typestatic;
    }
}
ChangeMicrophonePermissionsMessage.typestatic = "ChangeMicrophonePermissionsMessage";
ChangeMicrophonePermissionsMessage.subjectstatic = "general";
MessageFactory.register(ChangeMicrophonePermissionsMessage);
/**
 * Publishes the current microphone direction.
 */
export class CurrentMicrophoneDirectionMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.localDirection) + calcStringByteLength(this.remoteDirection);
    }
    constructor(localDirection, remoteDirection) {
        super();
        this.localDirection = localDirection;
        this.remoteDirection = remoteDirection;
    }
    serialization(stream) {
        this.localDirection = stream.utf16StringSafe(this.localDirection);
        this.remoteDirection = stream.utf16StringSafe(this.remoteDirection);
    }
    static is(msg) {
        return msg.type === CurrentMicrophoneDirectionMessage.typestatic;
    }
}
CurrentMicrophoneDirectionMessage.typestatic = "CurrentMicrophoneDirectionMessage";
CurrentMicrophoneDirectionMessage.subjectstatic = "general";
MessageFactory.register(CurrentMicrophoneDirectionMessage);
/**
 * Enables or disables orbit-control rotation.
 */
export class EnableOrbitControlsRotationMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(enabled) {
        super();
        this.enabled = enabled;
    }
    serialization(stream) {
        this.enabled = stream.boolean(this.enabled);
    }
    static is(msg) {
        return msg.type === EnableOrbitControlsRotationMessage.typestatic;
    }
}
EnableOrbitControlsRotationMessage.typestatic = "EnableOrbitControlsRotationMessage";
EnableOrbitControlsRotationMessage.subjectstatic = "general";
MessageFactory.register(EnableOrbitControlsRotationMessage);
/**
 * Requests whether XR presenting is currently active.
 */
export class IsXrPresentingRequestMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === IsXrPresentingRequestMessage.typestatic;
    }
}
IsXrPresentingRequestMessage.typestatic = "IsXrPresentingRequestMessage";
IsXrPresentingRequestMessage.subjectstatic = "general";
MessageFactory.register(IsXrPresentingRequestMessage);
/**
 * Returns whether XR presenting is currently active.
 */
export class IsXrPresentingAnswerMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(xrPresenting) {
        super();
        this.xrPresenting = xrPresenting;
    }
    serialization(stream) {
        this.xrPresenting = stream.boolean(this.xrPresenting);
    }
    static is(msg) {
        return msg.type === IsXrPresentingAnswerMessage.typestatic;
    }
}
IsXrPresentingAnswerMessage.typestatic = "IsXrPresentingAnswerMessage";
IsXrPresentingAnswerMessage.subjectstatic = "general";
MessageFactory.register(IsXrPresentingAnswerMessage);
/**
 * Requests the underlying Object3D for a model.
 */
export class RequestObject3DMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestObject3DMessage.typestatic;
    }
}
RequestObject3DMessage.typestatic = "RequestObject3DMessage";
RequestObject3DMessage.subjectstatic = "general";
MessageFactory.register(RequestObject3DMessage);
/**
 * Returns the underlying Object3D for a model.
 */
export class RequestObject3DAnswerMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + calcObjectByteLength(this.object);
    }
    constructor(uid, object) {
        super();
        this.uid = uid;
        this.object = object;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.object = stream.objectSafe(this.object);
    }
    static is(msg) {
        return msg.type === RequestObject3DAnswerMessage.typestatic;
    }
}
RequestObject3DAnswerMessage.typestatic = "RequestObject3DAnswerMessage";
RequestObject3DAnswerMessage.subjectstatic = "general";
MessageFactory.register(RequestObject3DAnswerMessage);
